import React, { useState, useContext } from 'react';
import {
    Button,
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    DialogContent,
    Tooltip,
    makeStyles,
    shorthands,
    Toaster,
    useToastController,
    ToastTitle,
    Toast,
    useId,
    Textarea,
} from '@fluentui/react-components';
import {
    ThumbLikeRegular,
    ThumbDislikeRegular,
    DismissRegular,
    ShareAndroidRegular,
    PersonSupportRegular,
} from '@fluentui/react-icons';
import { AppInsightsContext } from '../../utils/appInsights';
import { useUser } from '../../utils/UserContext';

interface FeedbackProps {
    confidenceText?: string;
    errorMessage?: string;
    sessionId: string;
}

const useStyles = makeStyles({
    buttonGroup: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: '100%',
        gap: '8px',
    },
    confidenceButton: {
        border: `2px solid`,
        background: 'transparent',
        borderRadius: '8px',
        color: '#ffffff',
        transform: 'scale(0.75)',
        opacity: '0.5',
        '&:active': {
            color: '#fff !important',
        },
        '&:hover': {
            color: '#fff',
        },
    },
    dialogContent: {
        ...shorthands.padding('16px'),
    },
});

const parseConfidenceLevel = (confidenceText?: string) => {
    if (confidenceText) {
        const firstWord = confidenceText.split(' ')[0];
        if (['Low', 'Medium', 'High', "Unapplicable"].includes(firstWord)) {
            return firstWord as 'Low' | 'Medium' | 'High' | 'Unapplicable';
        }
    }
    return undefined;
};

const getConfidenceColor = (
    confidenceLevel?: 'Low' | 'Medium' | 'High' | 'SCRIPT ERROR' | 'Unapplicable'
) => {
    if (confidenceLevel === 'SCRIPT ERROR') return '#ff0000'; // red for error
    switch (confidenceLevel) {
        case 'High':
            return '#1e612e'; // green
        case 'Medium':
            return '#ffd700'; // yellow
        case 'Low':
            return '#ff8c00'; // orange
        case 'Unapplicable':
            return '#808080'; // gray
        default:
            return '#FF0000';
    }
};

const FeedbackBar: React.FC<FeedbackProps> = ({
    confidenceText,
    errorMessage,
    sessionId,
}) => {
    const classes = useStyles();
    const confidenceLevel = errorMessage
        ? 'SCRIPT ERROR'
        : parseConfidenceLevel(confidenceText);
    const confidenceColor = getConfidenceColor(confidenceLevel);
    const tooltipText = errorMessage || confidenceText || '';

    const [likeActive, setLikeActive] = useState(false);
    const [dislikeActive, setDislikeActive] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const user = useUser();

    const toasterId = useId('toaster');
    const { dispatchToast } = useToastController(toasterId);

    const appInsights = useContext(AppInsightsContext);

    const handleLikeClick = () => {
        setLikeActive(true);
        setDislikeActive(false);
        setDialogOpen(true);
    };

    const handleDislikeClick = () => {
        setLikeActive(false);
        setDislikeActive(true);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleSubmit = () => {
        appInsights.trackEvent(
            { name: 'Feedback' },
            {
                user: user?.username,
                sentiment: likeActive ? 'Like' : 'Dislike',
                feedback: feedbackText,
                sessionId: sessionId,
            }
        );
        setDialogOpen(false);
    };

    const handleDialogOpenChange = (event: any, data: { open: boolean }) => {
        setDialogOpen(data.open);
    };

    const handleShareClick = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            dispatchToast(
                <Toast>
                    <ToastTitle>Link copied to clipboard!</ToastTitle>
                </Toast>,
                { intent: 'success' }
            );
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    return (
        <div className={classes.buttonGroup}>
            {/* Like Button */}
            <Button
                icon={<ThumbLikeRegular />}
                aria-label="Rate Positive"
                appearance="subtle"
                onClick={handleLikeClick}
            />

            {/* Dislike Button */}
            <Button
                icon={<ThumbDislikeRegular />}
                aria-label="Rate Negative"
                appearance="subtle"
                onClick={handleDislikeClick}
            />

            {/* Share Button */}
            <Tooltip withArrow content={'Share Session'} relationship={'label'}>
                <Button
                    icon={<ShareAndroidRegular />}
                    aria-label="Share"
                    appearance="subtle"
                    onClick={handleShareClick}
                />
            </Tooltip>

            {/* Get Assistance Button */}
            <Tooltip withArrow content={'Get Assistance'} relationship={'label'}>
                <Button
                    icon={<PersonSupportRegular />}
                    aria-label="Get Assistance"
                    appearance="subtle"
                    onClick={() => {
                        const sessionUrl = `https://gsc.xboxlive.com/?sessionId=${sessionId}`;
                        const message = encodeURIComponent(`Hi can you help me with this session? ${sessionUrl}`);
                        window.open(
                            `https://teams.microsoft.com/l/chat/0/0?users=GSC_Notif_CORE@microsoft.com&message=${message}`,
                            '_blank'
                        );
                    }}
                />
            </Tooltip>

            {/* Confidence Button */}
            <Tooltip withArrow content={tooltipText} relationship={'label'}>
                <Button
                    appearance="transparent"
                    className={classes.confidenceButton}
                    style={{ borderColor: confidenceColor }}
                    aria-label="Confidence"
                >
                    {confidenceLevel === 'SCRIPT ERROR'
                        ? confidenceLevel
                        : `Confidence: ${confidenceLevel}`}
                </Button>
            </Tooltip>

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Provide Additional Feedback</DialogTitle>
                        <DialogContent className={classes.dialogContent}>
                            <Textarea
                                id="feedback"
                                placeholder="Your Feedback"
                                rows={4}
                                value={feedbackText}
                                onChange={(e) =>
                                    setFeedbackText(e.target.value)
                                }
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    fontFamily: 'Segoe UI',
                                    fontSize: '14px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                appearance="secondary"
                                onClick={handleDialogClose}
                                icon={<DismissRegular />}
                            >
                                Close
                            </Button>
                            <Button appearance="primary" onClick={handleSubmit}>
                                Submit
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            <Toaster toasterId={toasterId} />
        </div>
    );
};

export default FeedbackBar;
